import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { History, Download, HardHat } from "lucide-react";
import { exportCSV } from "../../lib/csvExport";

export default function PSAHistoryDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ action: "", q: "", from_date: "", to_date: "" });
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const load = async () => {
    setRows(null);
    const params = {};
    if (filters.action) params.action = filters.action;
    if (filters.q) params.q = filters.q;
    if (filters.from_date) params.from_date = filters.from_date;
    if (filters.to_date) params.to_date = filters.to_date;
    try {
      const { data } = await api.get("/inventory/psa-history", { params });
      setRows(data || []);
    } catch {
      toast.error("Eroare la încărcarea istoricului");
      setRows([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filters]);

  const exportCsv = () => {
    if (!rows || rows.length === 0) return;
    const header = ["Data", "Acțiune", "Echipament", "Utilizator", "Detalii"];
    const csv = [header.join(";")];
    for (const r of rows) {
      const details = r.message || r.note || JSON.stringify(r.changes || r.snapshot || {}).replaceAll(";", ",").slice(0, 200);
      const vals = [
        String(r.ts || "").slice(0, 19).replace("T", " "),
        labelAction(r.action),
        (r.item_name || "").replaceAll(";", ","),
        (r.user || r.actor_name || "").replaceAll(";", ","),
        details.replaceAll(";", ",").replaceAll("\n", " "),
      ];
      csv.push(vals.map((v) => `"${v}"`).join(";"));
    }
    const blob = new Blob(["\uFEFF" + csv.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `istoric-PSA-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  function labelAction(a) {
    return {
      create: "➕ Creare", update: "✏️ Modificare", delete: "🗑️ Ștergere",
      pickup: "📥 Preluare", handover: "📤 Predare", move: "🔀 Mutare",
      repair: "🔧 Reparație", issue: "🚚 Ieșire magazie",
      split_assign: "👤 Predare (split)",
    }[a] || a;
  }

  const actionBadge = (a) => {
    const cls = {
      create: "bg-emerald-100 text-emerald-700",
      update: "bg-blue-100 text-blue-700",
      delete: "bg-red-100 text-red-700",
      pickup: "bg-cyan-100 text-cyan-700",
      handover: "bg-orange-100 text-orange-700",
      move: "bg-purple-100 text-purple-700",
      repair: "bg-amber-100 text-amber-700",
      split_assign: "bg-orange-100 text-orange-700",
    }[a] || "bg-zinc-100 text-zinc-700";
    return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${cls}`}>{labelAction(a)}</span>;
  };

  // Grupare client-side a predărilor în masă (mai multe split_assign apropiate în timp,
  // efectuate de același user pentru același item parent → o singură intrare expandabilă).
  // De asemenea, ascunde evenimentele `create` automate generate de split (reason="Split din stoc...").
  const groupedRows = useMemo(() => {
    if (!rows) return null;
    // 1) filtrare zgomot: create-urile child generate de split (au reason "Split din stoc")
    const filtered = rows.filter((r) => {
      if (r.action === "create" && typeof r.reason === "string" && r.reason.startsWith("Split din stoc")) return false;
      return true;
    });
    const out = [];
    const WINDOW_MS = 60 * 1000; // 60s între evenimente consecutive
    let buf = null;
    const flush = () => {
      if (!buf) return;
      if (buf.events.length > 1) {
        out.push({
          __group: true,
          id: `grp-${buf.events[0].id || buf.firstTs}`,
          ts: buf.events[0].ts, // cea mai recentă (rows sortate desc)
          action: "split_assign",
          item_name: buf.events[0].item_name,
          user: buf.events[0].user,
          events: buf.events,
          total_qty: buf.events.reduce((s, e) => s + (parseFloat(e.quantity) || 0), 0),
          recipients_count: new Set(buf.events.map((e) => e.to_assignee_person_id || e.to_assignee)).size,
        });
      } else {
        out.push(buf.events[0]);
      }
      buf = null;
    };
    // rows sunt sortate desc dupa ts
    for (const r of filtered) {
      if (r.action !== "split_assign") { flush(); out.push(r); continue; }
      const ts = new Date(r.ts || 0).getTime();
      const key = `${r.user_id || r.user || ""}|${r.item_name || ""}|${r.psa_size || ""}`;
      if (buf && buf.key === key && Math.abs(buf.firstTs - ts) <= WINDOW_MS) {
        buf.events.push(r);
        buf.firstTs = ts;
      } else {
        flush();
        buf = { key, firstTs: ts, events: [r] };
      }
    }
    flush();
    return out;
  }, [rows]);

  const toggleGroup = (id) => setExpandedGroups((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const renderDetails = (r) => {
    if (r.action === "update" && r.changes) {
      return (
        <div className="text-[10px] space-y-0.5">
          {Object.entries(r.changes).map(([k, v]) => (
            <div key={k} className="font-mono">
              <span className="text-zinc-500">{k}:</span>{" "}
              <span className="text-red-600 line-through">{String(v.from ?? "—")}</span>{" → "}
              <span className="text-emerald-600 font-semibold">{String(v.to ?? "—")}</span>
            </div>
          ))}
        </div>
      );
    }
    if (r.action === "delete" && r.snapshot) {
      return (
        <div className="text-[10px] text-zinc-600 italic">
          Snapshot: {Object.entries(r.snapshot).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(", ")}
        </div>
      );
    }
    if (r.action === "create" && r.snapshot) {
      return (
        <div className="text-[10px] text-zinc-600">
          La locația <strong>{r.to_location || "—"}</strong>{r.snapshot.note ? ` · ${r.snapshot.note}` : ""}
        </div>
      );
    }
    if (r.message) return <div className="text-[11px] text-zinc-600 italic">{r.message}</div>;
    if (r.note) return <div className="text-[11px] text-zinc-600 italic">{r.note}</div>;
    return <span className="text-zinc-400 text-[10px]">—</span>;
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="psa-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-700">
            <History size={18}/> <HardHat size={16}/> Istoric PSA
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-zinc-500 -mt-2">
          Toate acțiunile (creare, modificare, transfer, ștergere) efectuate asupra echipamentelor PSA.
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <div>
            <Label className="small-label">Acțiune</Label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({ ...filters, action: e.target.value })}
              className="w-full h-9 border border-zinc-200 rounded px-2 text-xs"
            >
              <option value="">Toate</option>
              <option value="create">➕ Creare</option>
              <option value="update">✏️ Modificare</option>
              <option value="pickup">📥 Preluare</option>
              <option value="handover">📤 Predare</option>
              <option value="split_assign">👤 Predare (split/bulk)</option>
              <option value="move">🔀 Mutare</option>
              <option value="delete">🗑️ Ștergere</option>
            </select>
          </div>
          <div>
            <Label className="small-label">De la data</Label>
            <Input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="h-9 text-xs"/>
          </div>
          <div>
            <Label className="small-label">Până la data</Label>
            <Input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="h-9 text-xs"/>
          </div>
          <div>
            <Label className="small-label">Căutare</Label>
            <Input placeholder="Echipament / user…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="h-9 text-xs"/>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-zinc-500">
          <span>
            {rows === null ? "Se încarcă…" : (
              groupedRows && groupedRows.length !== rows.length
                ? `${groupedRows.length} grupuri · ${rows.length} evenimente`
                : `${rows.length} evenimente`
            )}
          </span>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows || rows.length === 0}>
            <Download size={12} className="mr-1"/> Export CSV
          </Button>
        </div>

        <div className="border border-zinc-200 rounded-md max-h-[420px] overflow-y-auto">
          {rows === null ? (
            <div className="p-6 text-center text-sm text-zinc-400">Se încarcă…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-zinc-500">Niciun eveniment înregistrat.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 sticky top-0">
                <tr className="text-left text-[10px] uppercase text-zinc-500 tracking-wider">
                  <th className="px-3 py-2 font-bold whitespace-nowrap">Data</th>
                  <th className="px-3 py-2 font-bold">Acțiune</th>
                  <th className="px-3 py-2 font-bold">Echipament</th>
                  <th className="px-3 py-2 font-bold">User</th>
                  <th className="px-3 py-2 font-bold">Detalii</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {groupedRows.map((r, i) => {
                  if (r.__group) {
                    const isOpen = expandedGroups.has(r.id);
                    return (
                      <React.Fragment key={r.id}>
                        <tr
                          className="hover:bg-orange-50 align-top cursor-pointer bg-orange-50/40"
                          onClick={() => toggleGroup(r.id)}
                          data-testid={`psa-hist-group-${r.id}`}
                        >
                          <td className="px-3 py-2 font-mono text-zinc-600 whitespace-nowrap">{String(r.ts || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="px-3 py-2">
                            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap bg-orange-200 text-orange-900">
                              📤 Predare bulk · {r.recipients_count} ang.
                            </span>
                          </td>
                          <td className="px-3 py-2 font-semibold">{r.item_name}</td>
                          <td className="px-3 py-2 text-zinc-600">{r.user || "—"}</td>
                          <td className="px-3 py-2">
                            <div className="text-[11px] text-orange-900 font-semibold flex items-center gap-2">
                              <span>{isOpen ? "▼" : "▶"}</span>
                              <span>Total {r.total_qty} buc. către {r.recipients_count} angajați — click pentru detalii</span>
                            </div>
                          </td>
                        </tr>
                        {isOpen && r.events.map((e, j) => (
                          <tr key={`${r.id}-${j}`} className="bg-zinc-50/60 align-top">
                            <td className="px-3 py-1.5 pl-8 font-mono text-zinc-500 text-[10px] whitespace-nowrap">└ {String(e.ts || "").slice(11, 16)}</td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 text-zinc-600">{e.psa_size ? `Mărime ${e.psa_size}` : ""}</td>
                            <td className="px-3 py-1.5 text-zinc-700 font-medium">→ {e.to_assignee || "(angajat)"}</td>
                            <td className="px-3 py-1.5 text-[11px] text-zinc-600">
                              <strong className="text-orange-700">{e.quantity} buc.</strong>
                              {e.reason ? <span className="italic text-zinc-500 ml-1">· {e.reason}</span> : null}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  }
                  return (
                    <tr key={r.id || i} className="hover:bg-zinc-50 align-top" data-testid={`psa-hist-row-${r.id || i}`}>
                      <td className="px-3 py-2 font-mono text-zinc-600 whitespace-nowrap">{String(r.ts || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-2">{actionBadge(r.action)}</td>
                      <td className="px-3 py-2 font-semibold">{r.item_name || "—"}</td>
                      <td className="px-3 py-2 text-zinc-600">{r.user || r.actor_name || "—"}{r.counterparty_name ? <span className="text-[10px] text-zinc-400 block">→ {r.counterparty_name}</span> : null}</td>
                      <td className="px-3 py-2 max-w-md">{renderDetails(r)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
